import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

// Custom components
import TableModel from "../../components/atoms/tables/TableModel";
import { ShowDialog } from "../../components/ShowDialog";
import { BackdropLoading } from "../../components/BackdropLoading";
import { Grid, useMediaQuery, useTheme } from "@mui/material";

// Styles
import {
  Container,
  CustomButtonV2,
  PageTitle
} from "../styles";

import { ColorsContext } from "../../Context/ColorsContext";

// API
import { FormApi } from "../../services/FormApi";

// Router
import { useNavigate, useLocation } from "react-router-dom";
import Router from "../../router";

// Icons
import { SaveAltRounded, AddCircleOutlineRounded } from "@mui/icons-material";

// Utils
import { CheckResponse } from "../../utils/checkResponse";
import { FormatDate } from "../../utils/formatDate";
import { useTranslation } from "react-i18next";

//Hooks
import { useDownloadExcel } from "../../Hooks/useDownloadExcel";

// Permissions
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../Context/PermissionsContext'
import { SearchBar } from "../../components/atoms/searchBars/SearchBar";

export const FormManagementV2 = (props) => {
  const { t } = useTranslation();
	const { colors } = React.useContext(ColorsContext);
  const ability = useAbility(AbilityContext);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const location = useLocation();
  const formApi = new FormApi();
	const { check } = new CheckResponse();
	const { format } = new FormatDate();
  const isFormV2 = location.pathname === "/form-management-v2"

  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filteredRequest, setFilteredRequest] = useState([]);
	const [dialog, setDialog] = useState({
		status: false,
		title: '',
		message: '',
		type: null
  });
  
  const [headers, setHeaders] = useState([]);

  //?? Prepare fields to download Excel
	const fieldsConfig = {
		[t('forms.formManagement.number')]: 'id',
		[t('forms.formManagement.formName')]: 'name',
		[t('forms.formManagement.workflow')]: (item) => item?.workflow?.name,
		[t('forms.formManagement.integrationKey')]: 'integrationKey',
		[t('forms.formManagement.lastUpdate')]: (item) => format({ date: item?.updatedAt, format: true }).toString()
	}

  //?? Custom Hook
  const { handleDownloadExcelList } = useDownloadExcel(
    filteredRequest,
    fieldsConfig,
    t("sidebar.formsv2")
  );

  useEffect(() => {
    returnHeaders();

    async function call() {
			await loadData();
    }
    call();
  }, [props.isDemo]);

  const returnHeaders = () => {
    let heads = [
      {
        id: 'id',
        label: 'forms.formManagement.number',
        center: false,
        disablePadding:true,
        sortArrow: true,
        isRender: true,
        link: true,
        sticky: true,
        direction: 'left',
      },
      {
        id: 'name',
        label: 'forms.formManagement.formName',
        center: false,
        disablePadding:true,
        sortArrow: true,
        isRender: true,
      },
      {
        id: 'workflow',
        label: 'forms.formManagement.workflow',
        center: false,
        disablePadding: true,
        sortArrow: true,
        type: 'object',
        keys: ['icon', 'name'],
        customSort: 'workflow.name',
      },
      {
        id: 'integrationKey',
        label: 'forms.formManagement.integrationKey',
        center: false,
        disablePadding:true,
        sortArrow: true,
        isRender: true,
      },
      {
        id: 'updatedAt',
        label: 'forms.formManagement.lastUpdate',
        center: false,
        disablePadding:true,
        sortArrow: true,
        type: 'date',
      }
    ];

    let newActions = [];
    if (ability.can("UpdateForm")) {
      newActions.push('edit');
    }
    if (ability.can("DeleteForm")) {
      newActions.push('delete');
    }

    if (newActions.length > 0) {
      heads.push(addActionsRow(newActions))
    }

    setHeaders(heads);
  }

  function addActionsRow(actions) {
    return {
      id: 'actions',
      label: 'common.actions.title',
      center: false,
      disablePadding: false,
      sortArrow: false,
      type: 'actions',
      sticky: true,
      direction: 'right',
      keys: actions
    }
  }

  async function loadData() {
		setLoading(true);
    let response = await formApi.list(null, null, "form_io");
    if (response.status === 200) {
      setData(response.data);
    }
		setLoading(false);
		return response.data || [];
  }

  async function handleCreateNew() {
    navigate(Router.appCreateFormTwo);
  }

  async function handleEdit(row) {
    navigate(Router.appCreateFormTwo, { state: { row: row } });
  }

  async function handleDelete(id) {
    setLoading(true);
    let response = await formApi.delete(id);
    let result = check(response);

    if (result.status) {
      setDialog({
        status: true,
        title: t("forms.formManagement.messages.deletedForm.title"),
        message: t("forms.formManagement.messages.deletedForm.message"),
        type: null,
      });
			await loadData();
    } else {
      toast.error(() => (
        <div>
          {t("forms.formManagement.messages.deletedForm.error")}
          <br />
          {result.errors}
        </div>
      ));
    }

    setLoading(false);
  }

  async function handleOpenDialog(row) {
    setDialog({
      status: true,
      title: t("forms.formManagement.messages.deletedForm.confirm"),
      message: t("forms.formManagement.messages.deletedForm.confirmMessage", {
        form: row.name,
      }),
      type: "confirmation",
      id: row.id,
    });
  }

  async function handleCloseDialog(value) {
    if (value) {
      setDialog({ ...dialog, status: false });
      await handleDelete(dialog.id);
    } else {
      setDialog({ status: false });
    }
  }

  return (
    <Container open={props.open} style={{ marginBottom: 0 }}>
      <Grid container 
          spacing={1}
          alignItems="center"
          mb={1}
          columns={{ xs: 12, sm: 12, md: 12, lg: 23, xl: 15 }}>
        <Grid item xs={12} sm={12} md={12} lg={6} xl={4}>
          <PageTitle>{t("forms.formManagement.title")}</PageTitle>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={8} xl={6}>
          <SearchBar
            rows={data}
            setFilteredRequest={setFilteredRequest}
            filterText={t('common.searchBars.forms')}
            filterKeys={['id', 'name', 'e?.workflow?.name', 'updatedAt']}
          />
        </Grid>

        {isDesktop &&
          <Grid item md={false} lg={1} xl={2}></Grid>
        }

        <Grid item xs={12} sm={6} md={6} lg={8} xl={3}
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {ability.can("CreateForm") && (
              <CustomButtonV2
                variant="contained"
                btnType="primary"
                onClick={handleCreateNew}
                startIcon={<AddCircleOutlineRounded />}
              >
                {t("forms.formManagement.createNewForm")}
              </CustomButtonV2>
            )}
            <CustomButtonV2
              variant="contained"
              startIcon={<SaveAltRounded />}
              onClick={handleDownloadExcelList}
            >
              {t("common.download")}
            </CustomButtonV2>
        </Grid>
      </Grid>

      <TableModel
        headers={headers}
        filteredRequest={filteredRequest}
        rows={data}
        // Handle edit, delete
        handleEdit={handleEdit}
        handleDelete={handleOpenDialog}
        isFormV2={isFormV2}
        isList={true}
        hideToolbar={true}
      />

      <BackdropLoading open={loading} />
      <ShowDialog
        openDialog={dialog.status}
        dialog={dialog}
        handleCloseDialog={handleCloseDialog}
      />
    </Container>
  );
};